import VideoModule from './videoModule';
import { debounce } from '../helpers';
import Player from '@vimeo/player';

type YouTubePlayer = {
  on: (event: string, callback: () => void) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  mute: () => void;
};

class ModalVideoModule extends VideoModule {
  modal?: HTMLDivElement;
  modalOverlay?: HTMLDivElement;
  setupReady: boolean = false;
  overlayListenerAdded: boolean = false;
  modalOpen: boolean = false;

  constructor(el: HTMLElement) {
    super(el);
    this.setupModal();
    this.addListeners();
  }

  setupDefaults(): void {
    super.setupDefaults();

    this.dom.noModalParent = (this.dom.videoPlaceholder as HTMLElement)
      ?.parentElement as HTMLDivElement;
  }

  addListeners(): void {
    super.addListeners();
    window.addEventListener('resize', debounce(this.handleResize.bind(this), 250));
    document.addEventListener('keydown', e => {
      this.handleKeyPress(e);
    });
  }

  handleKeyPress(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.modalOpen) {
      this.closeModal();
    }
  }

  handleResize() {
    const mediaQuery = window.matchMedia('(min-width: 1200px)');

    this.setupModal();

    if (this.setupReady && !mediaQuery.matches) {
      if (this.dom.videoPlaceholder && this.dom.noModalParent) {
        // Move the existing videoPlaceholder to the noModalParent
        const videoPlaceholder = this.modal?.querySelector('.video-placeholder') as HTMLElement;
        const noModalParent = this.dom.noModalParent as HTMLElement;

        // Check if videoPlaceholder already has a parent and remove it from there
        if (videoPlaceholder.parentElement) {
          videoPlaceholder.parentElement.removeChild(videoPlaceholder);
        }

        // Append to the new parent
        noModalParent.appendChild(videoPlaceholder);

        this.dom.videoPlaceholder = videoPlaceholder;
      }

      this.closeModal();
      this.setupReady = false;
    }
  }

  setupModal() {
    const mediaQuery = window.matchMedia('(min-width: 1200px)');
    const videoPlaceholder = this.el.querySelector('.video-placeholder') as HTMLElement;

    if (this.setupReady || !mediaQuery.matches) return;

    if (!this.modal || !this.modalOverlay) {
      this.modal = document.createElement('div');
      this.modal.className = 'video-modal';
      this.modalOverlay = document.createElement('div');
      this.modalOverlay.className = 'video-modal-overlay';

      document.body.appendChild(this.modalOverlay);
      document.body.appendChild(this.modal);
    }

    if (videoPlaceholder.parentElement) {
      videoPlaceholder.parentElement.removeChild(videoPlaceholder);
    }

    this.modal.appendChild(videoPlaceholder);
    this.dom.videoPlaceholder = videoPlaceholder;

    this.setupReady = true;

    if (this.isPlaying) this.showModal();

    if (this.overlayListenerAdded) return;
    this.modalOverlay.addEventListener('click', () => this.closeModal());
    this.overlayListenerAdded = true;
  }

  closeModal() {
    if (this.isPlaying) {
      this.handlePlayButtonClick();
    }

    if (this.provider === 'vimeo') {
      (this.player as Player).pause();
    } else if (this.provider === 'youtube') {
      (this.player as YouTubePlayer).pauseVideo();
    } else if (this.dom.localVideo) {
      (this.dom.localVideo as HTMLVideoElement).pause();
    }
    this.isPlaying = false;

    if (this.modal) this.modal.style.display = 'none';
    if (this.modalOverlay) this.modalOverlay.style.display = 'none';

    if (this.dom.backgroundImage)
      (this.dom.backgroundImage as HTMLImageElement).style.display = 'block';
    if (this.dom.playButton) (this.dom.playButton as HTMLElement).style.display = 'flex';

    document.body.classList.remove('no-scroll');
    this.modalOpen = false;
  }

  showModal() {
    if (this.modal) this.modal.style.display = 'flex';
    if (this.modalOverlay) this.modalOverlay.style.display = 'block';

    document.body.classList.add('no-scroll');
    this.modalOpen = true;
  }

  handlePlayButtonClick() {
    if (!this.videoReady) return;

    const mediaQuery = window.matchMedia('(min-width: 1200px)');

    if (mediaQuery.matches) this.showModal();

    if (this.provider === 'youtube') {
      super.handlePlayButtonClick();
      setTimeout(() => {
        (this.player as YouTubePlayer).playVideo();
      }, 500);
    } else {
      super.handlePlayButtonClick();
    }
  }
}

export default ModalVideoModule;
